/**
 * hls-reveal.js — Hilusoya premium scroll reveal
 *
 * Strategy
 * ────────
 * 1. Every .shopify-section below the fold gets data-hls-reveal → fades + lifts in.
 * 2. Grid / card children get data-hls-item + --hls-i index → staggered wave.
 * 3. Hero images / media containers get data-hls-media → subtle scale settle.
 * 4. Sections already in viewport on load skip animation entirely (no flash).
 * 5. Fires once per section, then unobserves (zero ongoing cost).
 * 6. Disabled in Shopify editor and when prefers-reduced-motion is set.
 */
(function () {
  'use strict';

  /* ── Guards ── */
  if (!('IntersectionObserver' in window)) return;
  if (document.documentElement.classList.contains('shopify-design-mode')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* ── Selectors ── */

  // Containers whose direct children should stagger
  var GRID_SELECTORS = [
    '.hls-ct__grid',
    '.product-grid',
    '.collection-grid',
    '.featured-collection__grid',
    '.blog-posts__grid',
    '.grid',
    '[data-grid]',
    '.favorite-products__slides',
  ].join(', ');

  // Children inside grids that get the stagger treatment
  var ITEM_SELECTORS = [
    '.hls-card',
    '.card-wrapper',
    '.product-card-wrapper',
    '.grid__item',
    '.blog-post-card',
    '.collection-card',
  ].join(', ');

  // Media elements that get the subtle scale
  var MEDIA_SELECTORS = [
    '.banner__media',
    '.hero__media',
    '.slideshow__slide-image',
    '.media',
    '.deferred-media',
    '.favorite-products__hero',
    '.favorite-products__card-media',
  ].join(', ');

  // Sections to never animate (structural chrome)
  var SKIP_PATTERN = /header|footer|announcement|cookie|password/i;

  /* ── Tag a section and its children ── */
  function tagSection(section) {
    if (SKIP_PATTERN.test(section.id || '')) return false;

    // Tag card/grid children for stagger
    var grids = section.querySelectorAll(GRID_SELECTORS);
    grids.forEach(function (grid) {
      var items = grid.querySelectorAll(ITEM_SELECTORS);
      // Cap stagger at 6 items so it doesn't feel slow on large grids
      items.forEach(function (item, idx) {
        item.setAttribute('data-hls-item', '');
        item.style.setProperty('--hls-i', Math.min(idx, 6));
      });
    });

    // Tag media for scale effect
    var mediaEls = section.querySelectorAll(MEDIA_SELECTORS);
    mediaEls.forEach(function (el) {
      el.setAttribute('data-hls-media', '');
    });

    section.setAttribute('data-hls-reveal', '');
    return true;
  }

  /* ── Reveal ── */
  function reveal(section) {
    // Force a reflow so the browser registers the start state before transitioning
    void section.offsetHeight;
    section.classList.add('hls-revealed');
  }

  /* ── Observer ── */
  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    },
    {
      // Trigger when 5% of the section enters the viewport,
      // but pull the trigger line 60px up from the very bottom edge
      // so the animation starts just before the section hits the bottom of screen.
      threshold: 0.05,
      rootMargin: '0px 0px -60px 0px',
    }
  );

  /* ── Init ── */
  function init() {
    var sections = document.querySelectorAll('.shopify-section');

    sections.forEach(function (section) {
      var rect = section.getBoundingClientRect();
      var viewH = window.innerHeight || document.documentElement.clientHeight;

      // Already fully or mostly in the viewport on page load → skip animation,
      // reveal immediately so above-the-fold content is never hidden.
      if (rect.top < viewH * 0.9) {
        return;
      }

      if (tagSection(section)) {
        observer.observe(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Shopify editor: re-tag sections added/reloaded via the editor ── */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target;
    if (!section || SKIP_PATTERN.test(section.id || '')) return;
    // In the editor, just make everything visible immediately
    section.classList.add('hls-revealed');
  });

})();
